import { Variants } from 'motion/react';

import { CircleDecorator, QuadrantDecorator } from '@natu/ui/Decorators/DynamicDecorator';
import { MotionWrapper } from '@natu/ui/Decorators/MotionWrapper';
import { cn } from '@natu/utils/cn';
import { DEFAULT_EASE } from '@natu/utils/consts';

import {
  type BlokItem,
  DynamicRender,
  resolveStoryblokStyles,
  sbEditable,
  type SBProps,
  type Spacing,
} from '../../../../utils';

interface SBHowWeSupportYouPatternProps {
  quadrantItems?: BlokItem[];
  mtMobile?: Spacing;
  mtTablet?: Spacing;
  mtDesktop?: Spacing;
  mbMobile?: Spacing;
  mbTablet?: Spacing;
  mbDesktop?: Spacing;
}

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  whileInView: {
    opacity: 1,
    transition: {
      ease: DEFAULT_EASE,
      staggerChildren: 0.1,
    },
  },
};

const itemVariants: Variants = {
  hidden: { y: 20, opacity: 0 },
  whileInView: {
    opacity: 1,
    y: 0,
    transition: {
      ease: DEFAULT_EASE,
    },
  },
};

export const SBHowWeSupportYouPattern = ({ blok }: SBProps<SBHowWeSupportYouPatternProps>) => {
  const { quadrantItems, mbMobile, mbTablet, mbDesktop, mtMobile, mtTablet, mtDesktop } = blok;

  const className = resolveStoryblokStyles({
    mt: mtMobile,
    mtTablet,
    mtDesktop,
    mb: mbMobile,
    mbTablet,
    mbDesktop,
  });

  return (
    <MotionWrapper
      variants={containerVariants}
      className={cn(className)}
      initial="hidden"
      viewport={{ once: true }}
      whileInView="whileInView"
      {...sbEditable(blok)}
    >
      {/* Mobile View */}
      <div className="grid w-full grid-cols-[repeat(2,_minmax(0,_256px))] items-center justify-center lg:hidden">
        <MotionWrapper variants={itemVariants}>
          <DynamicRender data={quadrantItems?.[1]} />
        </MotionWrapper>
        <MotionWrapper variants={itemVariants}>
          <DynamicRender data={quadrantItems?.[5]} />
        </MotionWrapper>
        <MotionWrapper variants={itemVariants}>
          <DynamicRender data={quadrantItems?.[4]} />
        </MotionWrapper>
        <div className="grid items-end justify-end">
          <QuadrantDecorator className="text-primary-700 h-[38px] w-[38px] -rotate-90" />
        </div>
        <DynamicRender data={quadrantItems?.[3]} />
        <DynamicRender data={quadrantItems?.[0]} />
        <div>
          <QuadrantDecorator className="text-naturaily-airborne h-[32px] w-[32px] rotate-90" />
        </div>
        <DynamicRender data={quadrantItems?.[2]} />
      </div>

      {/* Desktop View */}
      <div className="hidden grid-cols-[repeat(5,_minmax(0,_256px))] lg:grid">
        <div />
        <MotionWrapper variants={itemVariants}>
          <DynamicRender data={quadrantItems?.[0]} />
        </MotionWrapper>
        <div>
          <MotionWrapper className="ml-16 flex">
            <CircleDecorator className="text-primary-700 h-[62px] w-[62px]" />
            <QuadrantDecorator className="text-naturaily-airborne h-[62px] w-[62px] -rotate-90" />
          </MotionWrapper>
        </div>
        <div className="grid items-end justify-end">
          <MotionWrapper className="max-w-[158px]" variants={itemVariants}>
            <DynamicRender data={quadrantItems?.[1]} />
          </MotionWrapper>
        </div>
        <div className="grid items-end">
          <MotionWrapper className="max-w-[210px]" variants={itemVariants}>
            <DynamicRender data={quadrantItems?.[2]} />
          </MotionWrapper>
        </div>

        <MotionWrapper variants={itemVariants}>
          <DynamicRender data={quadrantItems?.[3]} />
        </MotionWrapper>
        <MotionWrapper className="ml-14 grid items-end" variants={itemVariants}>
          <QuadrantDecorator className="text-naturaily-butterscotch h-[38px] w-[38px] rotate-90" />
        </MotionWrapper>
        <MotionWrapper variants={itemVariants}>
          <DynamicRender data={quadrantItems?.[4]} />
        </MotionWrapper>
        <MotionWrapper variants={itemVariants}>
          <DynamicRender data={quadrantItems?.[5]} />
        </MotionWrapper>
        <MotionWrapper className="grid items-end justify-end" variants={itemVariants}>
          <QuadrantDecorator className="text-primary-700 h-[80px] w-[80px] -rotate-90" />
        </MotionWrapper>
      </div>
    </MotionWrapper>
  );
};
